import React from 'react';
import { Link } from 'react-router-dom';
import { useReportsAccess } from '../components/ReportsGate';
import RequestReportForm from '../components/RequestReportForm';

// Report types and who can reach them.
//
// `access: 'all'`      — any authenticated org (suppliers included).
// `access: 'reports'`  — gated by useReportsAccess()/requireReportsAccess.
//
// Gated cards stay clickable when locked: the destination renders ReportsGate,
// which explains exactly why and what to do about it. A disabled tile that says
// nothing is a dead end.
const REPORT_TYPES = [
  {
    id: 'product-comparison',
    title: 'Product Comparison Report',
    description:
      'Put up to three products side by side — materials, manufacturing, transport, and total carbon — and download it as a PDF.',
    icon: '⚖️',
    href: '/reports/product-comparison',
    access: 'all',
  },
  {
    id: 'customer-footprint',
    title: 'Customer Footprint Report',
    description:
      "Send a customer a beautiful email summarising the carbon footprint of the products they ordered from you.",
    icon: '🌱',
    href: '/reports/customer-footprint',
    access: 'reports',
  },
];

function ReportCard({ report, locked, lockLabel }) {
  return (
    <Link to={report.href} className="text-decoration-none">
      <div
        className="card h-100 shadow-sm border-0"
        style={{ borderRadius: 12, transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '';
        }}
      >
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div style={{ fontSize: 28, lineHeight: 1 }} aria-hidden>{report.icon}</div>
            {locked && (
              <span
                style={{
                  background: '#f4f4f5', color: '#52525b', borderRadius: 999,
                  padding: '3px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                }}
              >
                {lockLabel}
              </span>
            )}
          </div>
          <h2 className="h5 fw-bold mt-3 mb-2 text-dark">{report.title}</h2>
          <p className="text-muted mb-0" style={{ fontSize: 14 }}>{report.description}</p>
        </div>
        <div className="card-footer bg-transparent border-0 pt-0 pb-3 px-4">
          <span className="text-primary fw-semibold" style={{ fontSize: 14 }}>
            {locked ? 'Learn more →' : 'Open →'}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Reports() {
  const access = useReportsAccess();

  // Only the gated report types care about this; the comparison report doesn't.
  const lockLabel = access.status === 'supplier' ? 'Coming soon' : 'Paid plan';
  const isLocked = (report) => report.access === 'reports' && access.status !== 'allowed';

  return (
    <div className="p-3" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div className="d-flex flex-column gap-1 mb-4">
        <h1 className="fw-bold mb-0">Reports</h1>
        <p className="text-muted mb-0">
          Compare your products and send branded carbon footprint reports to your customers.
        </p>
      </div>

      <div className="row g-3">
        {REPORT_TYPES.map((report) => (
          <div className="col-12 col-md-6 col-lg-4" key={report.id}>
            <ReportCard report={report} locked={isLocked(report)} lockLabel={lockLabel} />
          </div>
        ))}

        <div className="col-12 col-md-6 col-lg-4">
          <div
            className="card h-100 border-0"
            style={{ borderRadius: 12, background: '#f4f4f5', border: '1px dashed #d4d4d8' }}
          >
            <div className="card-body p-4 d-flex flex-column justify-content-center align-items-center text-center">
              <div className="text-muted" style={{ fontSize: 13 }}>
                More report types coming soon.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 48, maxWidth: 720 }}>
        <h2 className="h6 fw-bold text-muted mb-3" style={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 12 }}>
          Need something else?
        </h2>
        <RequestReportForm
          heading="Request a custom report"
          helper="Tell us what you'd find useful, we use this to prioritise the next report types we build."
        />
      </div>
    </div>
  );
}
